// ─── MD3 Expressive Menu — Framer Motion Animation Variants ──────────────────
// FastSpatial: md.sys.motion.spring.fast.spatial
//   MD3: stiffness=800, dampingRatio=0.6 → Framer damping ≈ 33.94
// FastEffects: md.sys.motion.spring.fast.effects (CSS fallback used for exit)
//   MD3: duration=150ms, cubic-bezier(0.31, 0.94, 0.34, 1.00)

import type { Variants } from "motion/react";
import {
	FAST_EFFECTS_SPRING,
	FAST_SPATIAL_SPRING,
} from "../shared/motion-tokens";

export { FAST_EFFECTS_SPRING, FAST_SPATIAL_SPRING };

/** FastEffects CSS-fallback transition — used for opacity/exit animations */
export const FAST_EFFECTS_TRANSITION = {
	duration: 0.15,
	ease: [0.31, 0.94, 0.34, 1.0] as [number, number, number, number],
} as const;

// ─── Menu popup container ─────────────────────────────────────────────────────

/**
 * Enter/exit animation for the menu popup container.
 * Scale from 0.8→1.0 with FastSpatial spring.
 * Transform-origin is driven by the Radix CSS variable
 * `--radix-dropdown-menu-content-transform-origin`.
 */
export const MENU_CONTAINER_VARIANTS: Variants = {
	hidden: {
		opacity: 0,
		scale: 0.8,
	},
	visible: {
		opacity: 1,
		scale: 1,
		transition: FAST_SPATIAL_SPRING,
	},
	exit: {
		opacity: 0,
		scale: 0.8,
		transition: FAST_EFFECTS_TRANSITION,
	},
};

// ─── Selected check icon ──────────────────────────────────────────────────────

/** Size of the check icon in px (20dp per SegmentedMenuTokens.ItemLeadingIconSize) */
export const MENU_CHECK_ICON_SIZE = 20;

/**
 * Expand/collapse animation for the check icon that appears when a MenuItem is
 * selected. Uses horizontal expansion (width + marginInlineEnd) with FastSpatial.
 *
 * ONLY used for the animated check ↔ selectedIcon swap in selectable items.
 * Regular (static) leading icons should NOT use these variants.
 */
export const CHECK_ICON_VARIANTS: Variants = {
	hidden: {
		opacity: 0,
		width: 0,
	},
	visible: {
		opacity: 1,
		width: MENU_CHECK_ICON_SIZE,
		transition: FAST_SPATIAL_SPRING,
	},
	exit: {
		opacity: 0,
		width: 0,
		transition: FAST_EFFECTS_TRANSITION,
	},
};

// ─── SubMenu content ──────────────────────────────────────────────────────────

/** SubMenu popup uses the same FastSpatial/FastEffects pattern as root menu */
export const SUBMENU_CONTAINER_VARIANTS: Variants = {
	hidden: {
		opacity: 0,
		scale: 0.9,
		x: -4,
	},
	visible: {
		opacity: 1,
		scale: 1,
		x: 0,
		transition: FAST_SPATIAL_SPRING,
	},
	exit: {
		opacity: 0,
		scale: 0.9,
		x: -4,
		transition: FAST_EFFECTS_TRANSITION,
	},
};
